import { Injectable } from "@angular/core";
import * as moment from "moment";
import { utc } from "moment";

@Injectable({ providedIn: 'root' })
export class DateService
{

    /**
   * Calculates the start and end time strings for job queries based on
   * either an 'upcomingCount' (for a range of days from today) or
   * a specific 'startTime' string (for a single day).
   *
   * @returns An object containing the calculated startTime and endTime as ISO strings.
   */
  public calculateTimeRanges(startTime?: string, endTime?: string, upcomingCount?: number, useCurrentTime: boolean = true): { startTime: string, endTime: string} {
    let startTimeUpdatedString: string;
    let endTimeUpdatedString: string;

    // Check if upcomingCount is a positive number to determine the logic path
    if (upcomingCount && upcomingCount > 0) {
      // Scenario: Calculate time range for upcoming days from today
        const today = new Date();
        
        // Create a clean date object for today (no time component)
        const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        startTimeUpdatedString = this._getLocalDateAsStartOfDayISO(startDate, useCurrentTime);
        
        // Calculate end date from the clean start date
        const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        endDate.setDate(endDate.getDate() + upcomingCount - 1);
        
        endTimeUpdatedString = this._getLocalDateAsEndOfDayISO(endDate);

    } else {
      // Scenario: Calculate time range for a specific day based on this.startTime
      // This assumes this.startTime is a string like 'YYYY-MM-DD'

      // Set start time to local midnight of the specified start date
      startTimeUpdatedString = this._getLocalDateAsStartOfDayISO(startTime!, useCurrentTime);

      // Set end time to local end of day for the *same* specified start date
      endTimeUpdatedString = this._getLocalDateAsEndOfDayISO(endTime!);
    }

    return { startTime: startTimeUpdatedString, endTime: endTimeUpdatedString };
  }

  /**
   * Helper method to get the ISO string for the start of a day
   * (midnight local time, converted to UTC).
   *
   * @param dateInput The date, either as a 'YYYY-MM-DD' string or a Date object.
   * @returns The ISO 8601 string representing local midnight of the given date in UTC.
   * (e.g., for '2025-07-31' in EDT (UTC-4), returns '2025-07-31T04:00:00.000Z')
   */
  private _getLocalDateAsStartOfDayISO(dateInput: string | Date, useCurrentTime: boolean): string {
    let dateObj: Date;

    if (typeof dateInput === 'string') {
      // Parse 'YYYY-MM-DD' string into year, month, day components.
      // IMPORTANT: Assumes dateInput is always in 'YYYY-MM-DD' format.
      const dateParts = dateInput.split('-');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed in Date constructor (Jan=0, Dec=11)
      const day = parseInt(dateParts[2], 10);

      // Create Date object using year, month, day arguments.
      // This tells JavaScript to interpret these values in the LOCAL timezone.
      dateObj = new Date(year, month, day);
    } else {

      // If a Date object is passed, create a new one to avoid modifying the original reference.
      dateObj = new Date(dateInput);
    }

    // Set the time components to local midnight (00:00:00.000)
    if (useCurrentTime) {
      const now = new Date();
      dateObj.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    } else {
      dateObj.setHours(0, 0, 0, 0);
    }

    // Convert to ISO string, which will be in UTC.
    // For EDT (UTC-4), local 00:00:00 becomes UTC 04:00:00.
    return dateObj.toISOString();
  }

  /**
   * Helper method to get the ISO string for the end of a day
   * (23:59:59.999 local time, converted to UTC).
   *
   * @param dateInput The date, either as a 'YYYY-MM-DD' string or a Date object.
   * @returns The ISO 8601 string representing local end of day of the given date in UTC.
   * (e.g., for '2025-07-31' in EDT (UTC-4), returns '2025-08-01T03:59:59.999Z')
   */
  private _getLocalDateAsEndOfDayISO(dateInput: string | Date): string {
    let dateObj: Date;

    if (typeof dateInput === 'string') {
      // Parse 'YYYY-MM-DD' string into year, month, day components.
      const dateParts = dateInput.split('-');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2], 10);

      // Create Date object in LOCAL timezone.
      dateObj = new Date(year, month, day);
    } else {
      // If a Date object is passed, create a new one.
      dateObj = new Date(dateInput);
    }

    // Set the time components to local end of day (23:59:59.999)
    dateObj.setHours(23, 59, 59, 999);

    // Convert to ISO string, which will be in UTC.
    // For EDT (UTC-4), local 23:59:59.999 becomes UTC 03:59:59.999 of the *next* day.
    return dateObj.toISOString();
  }

  // public getLocalTimeDate(dateString: string, timeString: string) : Date
  // {
  //   var localUTCString = this.getLocalTimeUTC(dateString, timeString);
  //   return utc(localUTCString)!.local()!.toDate();
  // }

  public getLocalDateTimeStringToDate(localDateTimeString: string) : Date
  {
    const [datePart, timePart] = localDateTimeString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);

    let hoursFound: number = 0;
    let minutesFound: number = 0;
    if(timePart) {
        const [hours, minutes] = timePart.split(':').map(Number);
        hoursFound = hours;
        minutesFound = minutes;
    }

    const dateObj = new Date(year, month - 1, day, hoursFound, minutesFound); // month is 0-indexed

    return dateObj;
  }

  // go from a UTC string to a local date
  public getLocalTime(stringTimeUtc: string) : Date
  {
    return utc(stringTimeUtc)!.local()!.toDate();
  }

  /**
   * Converts a local date and time into an ISO-formatted string
   * @param localTimeDateString - The date in YYYY-MM-DD format
   * @param localTimeString - The time in HH:mm:ss.SSS format
   * @param includeTimezone - Optional. If true, includes timezone offset in output
   * @returns A string in format YYYY-MM-DDTHH:mm:ss.SSS[±HH:mm] representing the local date and time
   * @description
   * - Preserves the local date/time without timezone conversions
   * - Does not modify the day value due to timezone shifts
   * - Returns ISO format string with optional timezone suffix
   * @example
   * getLocalTimeString('2025-06-20', '14:30:00.000')
   * // returns '2025-06-20T14:30:00.000'
   * getLocalTimeString('2025-06-20', '14:30:00.000', true)
   * // returns '2025-06-20T14:30:00.000-07:00'
   */
  getLocalTimeString(localTimeDateString: string, localTimeString: string, includeTimezone: boolean = false): string {
    // Combine date and time strings
    const dateTimeString = `${localTimeDateString}T${localTimeString}`;
    
    // Format with or without timezone based on parameter
    return moment(dateTimeString).format(
        includeTimezone ? 'YYYY-MM-DDTHH:mm:ss.SSSZ' : 'YYYY-MM-DDTHH:mm:ss.SSS'
    );
  }

  getLocalTimeStringv2(localTimeDateString: string): string {

    const date = this.getLocalDateTimeStringToDate(localTimeDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const localTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;

    return localTimeString;
  }

getLocalTimeUTC(localTimeDateString: string, localTimeString: string = "00:00"): string {
    try {
        // Handle empty or invalid input
        if (!localTimeDateString) {
            throw new Error("Date string is required");
        }

        // If only time is provided, use today's date
        if (/^\d{2}:\d{2}$/.test(localTimeDateString)) {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            localTimeString = localTimeDateString;
            localTimeDateString = `${year}-${month}-${day}`;
        }

        // Check if the input contains a 'T' separator (datetime-local format)
        if (localTimeDateString.includes('T')) {
            const [datePart, timePart] = localTimeDateString.split('T');
            localTimeString = timePart || "00:00";
            localTimeDateString = datePart;
        }

        // Try parsing the date directly first
        const parsedDate = new Date(localTimeDateString);
        if (!isNaN(parsedDate.getTime())) {
            const [hours, min, sec = 0, ms = 0] = localTimeString.split(':').map(Number);
            parsedDate.setHours(hours ?? 0, min ?? 0, sec ?? 0, ms ?? 0);
            return parsedDate.toISOString();
        }

        // Split date components and convert to numbers
        const [year, month, day] = localTimeDateString.split('-').map(Number);
        const [hours, min, sec = 0, ms = 0] = localTimeString.split(':').map(Number);
        
        // Create date in local timezone
        const localDate = new Date(year, month - 1, day);
        
        // Validate the date is valid
        if (isNaN(localDate.getTime())) {
            throw new Error(`Invalid date components: year=${year}, month=${month}, day=${day}`);
        }
        
        // Set time components
        localDate.setHours(hours ?? 0, min ?? 0, sec ?? 0, ms ?? 0);
        
        return localDate.toISOString();
    } catch (error) {
        console.error('Error in getLocalTimeUTC:', error);
        throw error;
    }
}

      /**
     */
    getStartOfDayUTC(dateString: string): string {
      // Validate date string format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          throw new Error('Date string must be in format "YYYY-MM-DD"');
      }

      // Split date components and convert to numbers
      const [year, month, day] = dateString.split('-').map(Number);
      
      // Create date in local timezone
      // Note: months are 0-based in JavaScript Date
      const localDate = new Date(year, month - 1, day);
      
      // Set to end of day in local timezone (23:59:59.999)
      localDate.setHours(0, 0, 0, 0);
      
      return localDate.toISOString();
    }

    /**
     */
    getEndOfDayUTC(dateString: string): string {
      let localDate: Date;
      
      if (dateString.includes('T')) {
        const utcDate = new Date(dateString);
        
        // If the string has a "Z" or timezone offset, it's UTC - convert to local date
        // If no "Z", it's already treated as local time
        if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
          // UTC string - extract local date components
          localDate = new Date(
            utcDate.getFullYear(),
            utcDate.getMonth(),
            utcDate.getDate()
          );
        } else {
          // Already local time - just use it
          localDate = utcDate;
        }
      } else {
        // Plain "YYYY-MM-DD" - original behavior
        const [year, month, day] = dateString.split('-').map(Number);
        localDate = new Date(year, month - 1, day);
      }
      
      localDate.setHours(23, 59, 59, 999);
      return localDate.toISOString();
    }

    public formatDateForInput(isoString: string): string {
      if (!isoString) return '';
      
      const date = new Date(isoString);
      
      // Pad with leading zeros and ensure YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
}